import { useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box, Typography } from "@mui/material";
import "./RichTextEditor.scss";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "link",
];

/**
 * Cleans HTML for saving by removing:
 * - trailing empty paragraphs
 * - empty paragraphs before <ul> or <ol>
 * @param {string} html - The HTML content to clean
 * @returns {string} - Cleaned HTML content
 */
export const cleanRichTextForSave = (html = "") => {
  if (!html) return "";

  let cleaned = html.trim();

  let prev;
  do {
    prev = cleaned;
    cleaned = cleaned.replace(
      /(<p>\s*(<br\s*\/?>)?\s*<\/p>\s*)+(<[uo]l)/gi,
      "$3",
    );
    cleaned = cleaned.replace(/(<br\s*\/?>\s*)+(<[uo]l)/gi, "$2");
  } while (cleaned !== prev);

  do {
    prev = cleaned;
    cleaned = cleaned.replace(/(<p>\s*(<br\s*\/?>)?\s*<\/p>\s*)+$/gi, "");
    cleaned = cleaned.replace(/(<br\s*\/?>\s*)+$/gi, "");
  } while (cleaned !== prev);

  return cleaned;
};

export const RichTextEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  touched,
  label,
  required = false,
  className = "",
  ...props
}) => {
  const hasError = touched && error;
  const quillRef = useRef(null);
  const isCleaningRef = useRef(false);

  const handleBlur = useCallback(
    (previousRange, source, editor) => {
      if (isCleaningRef.current) {
        return;
      }

      const rawHtml = editor.getHTML();
      const cleaned = cleanRichTextForSave(rawHtml);

      if (cleaned !== rawHtml) {
        isCleaningRef.current = true;
        onChange(cleaned);
        requestAnimationFrame(() => {
          isCleaningRef.current = false;
        });
      }

      if (onBlur) {
        if (onBlur.length === 0) {
          onBlur();
        } else {
          onBlur(previousRange, source, editor);
        }
      }
    },
    [onChange, onBlur],
  );

  return (
    <Box className="rich-text-editor-container">
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          className="editor-label"
        >
          {label} {required && "*"}
        </Typography>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        onBlur={handleBlur}
        modules={quillModules}
        formats={quillFormats}
        placeholder={placeholder}
        className={`rich-text-editor ${
          hasError ? "rich-text-editor-error" : ""
        } ${className}`}
        {...props}
      />

      {hasError && (
        <Typography variant="caption" color="error" className="error-text">
          {error}
        </Typography>
      )}
    </Box>
  );
};
